// Pure logic for lead-flow-monitor (#1284). No Deno or esm imports, so the
// vitest suite (src/test/unit/lead-flow-monitor.test.ts) imports it directly.
// Window math, the Meta insights aggregation, the spend gate and the incident
// state machine all live here; index.ts only does I/O.

export const HOUR_MS = 3_600_000;
export const DEFAULT_WINDOW_HOURS = 48;
export const DEFAULT_MIN_SPEND_CENTS = 1;
export const MAX_WINDOW_HOURS = 168;
export const MAX_INSIGHT_PAGES = 5;
export const SETTINGS_KEY = "lead_flow_monitor";
export const LEAD_TABLES = ["events", "landing_page_leads", "menu_inquiries"] as const;
// PostgREST .or() filter for a paid lead row: a non-empty fbclid, unless the
// row carries the /ig bio tag (Instagram adds fbclid to organic bio clicks,
// #1576), or a paid_social medium. ilike ignores case, and `_` is SQL's
// one-character wildcard, so paid_social also matches paid-social. The
// is.null arm keeps untagged fbclid rows: NOT (NULL ILIKE 'bio') is NULL.
export const PAID_FILTER =
  "and(fbclid.not.is.null,fbclid.neq.,or(utm_medium.is.null,utm_medium.not.ilike.bio))," +
  "utm_medium.ilike.paid_social,utm_medium.ilike.paidsocial";
const MICROS_PER_UNIT = 1_000_000;
const MICROS_PER_CENT = 10_000;

// ── Settings ────────────────────────────────────────────────────────────────

export interface MonitorSettings {
  windowHours: number;
  minSpendCents: number;
}

export type SettingsResult = { ok: true; settings: MonitorSettings } | { ok: false; message: string };

/** Parses app_settings.lead_flow_monitor. A missing row means defaults; anything malformed fails closed. */
export function parseMonitorSettings(raw: string | null | undefined): SettingsResult {
  const defaults = { windowHours: DEFAULT_WINDOW_HOURS, minSpendCents: DEFAULT_MIN_SPEND_CENTS };
  if (raw === null || raw === undefined || raw.trim() === "") return { ok: true, settings: defaults };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, message: "lead_flow_monitor setting is not valid JSON" };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, message: "lead_flow_monitor setting must be a JSON object" };
  }
  const obj = parsed as Record<string, unknown>;
  const windowHours = obj.window_hours === undefined ? defaults.windowHours : obj.window_hours;
  const minSpendCents = obj.min_spend_cents === undefined ? defaults.minSpendCents : obj.min_spend_cents;
  if (!Number.isInteger(windowHours) || (windowHours as number) < 1 || (windowHours as number) > MAX_WINDOW_HOURS) {
    return { ok: false, message: `window_hours must be a whole number from 1 to ${MAX_WINDOW_HOURS}` };
  }
  if (!Number.isInteger(minSpendCents) || (minSpendCents as number) < 0) {
    return { ok: false, message: "min_spend_cents must be a whole number of 0 or more" };
  }
  return { ok: true, settings: { windowHours: windowHours as number, minSpendCents: minSpendCents as number } };
}

// ── Window and time zones ───────────────────────────────────────────────────

export interface MonitorWindow {
  startMs: number;
  endMs: number;
  hours: number;
}

/** The last `hours` whole UTC hours before now, half-open [start, end). */
export function computeWindow(nowMs: number, hours: number): MonitorWindow {
  const endMs = Math.floor(nowMs / HOUR_MS) * HOUR_MS;
  return { startMs: endMs - hours * HOUR_MS, endMs, hours };
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function zonedParts(ms: number, tz: string): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  let fmt = formatters.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(tz, fmt);
  }
  const parts = Object.fromEntries(fmt.formatToParts(new Date(ms)).map((p) => [p.type, Number(p.value)]));
  return { y: parts.year, mo: parts.month, d: parts.day, h: parts.hour, mi: parts.minute, s: parts.second };
}

/** Offset of `tz` from UTC at instant `ms`, in milliseconds (EDT = -4h). */
function tzOffsetMs(ms: number, tz: string): number {
  const p = zonedParts(ms, tz);
  const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** The calendar date ("YYYY-MM-DD") of instant `ms` in `tz`. */
export function localDate(ms: number, tz: string): string {
  const p = zonedParts(ms, tz);
  return `${p.y}-${String(p.mo).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

/**
 * UTC instant of `hour`:00 on local `date` in `tz`. Guess with the offset at the
 * naive instant, then correct once. An ambiguous fall-back hour resolves to the
 * first (daylight) occurrence.
 */
export function zonedHourToUtcMs(date: string, hour: number, tz: string): number {
  const [y, mo, d] = date.split("-").map(Number);
  const naive = Date.UTC(y, mo - 1, d, hour);
  const first = tzOffsetMs(naive, tz);
  let utc = naive - first;
  const second = tzOffsetMs(utc, tz);
  if (second !== first) utc = naive - second;
  return utc;
}

/** Meta's day-granular time_range covering the window, in the account's time zone. */
export function insightsTimeRange(win: MonitorWindow, tz: string): { since: string; until: string } {
  return { since: localDate(win.startMs, tz), until: localDate(win.endMs - 1, tz) };
}

/** "13:00:00 - 13:59:59" → 13. */
export function parseHourBucket(bucket: unknown): number | null {
  if (typeof bucket !== "string") return null;
  const m = bucket.match(/^(\d{2}):00:00 - \d{2}:59:59$/);
  if (!m) return null;
  const h = Number(m[1]);
  return h >= 0 && h <= 23 ? h : null;
}

// ── Meta insights ───────────────────────────────────────────────────────────

/**
 * "0.322494" → 322494. Integer millionths of the currency unit, because Meta's
 * hourly spend carries up to 6 decimals and rounding each row to cents drifts.
 * A 7th decimal onward rounds half-up. null when not a plain non-negative amount.
 */
export function parseSpendMicros(value: unknown): number | null {
  if (value === undefined || value === null) return 0;
  const s = String(value).trim();
  const m = s.match(/^(\d+)(?:\.(\d+))?$/);
  if (!m) return null;
  const frac = m[2] ?? "";
  const micros = Number(m[1]) * MICROS_PER_UNIT + Number(frac.slice(0, 6).padEnd(6, "0")) + ((frac[6] ?? "0") >= "5" ? 1 : 0);
  return Number.isSafeInteger(micros) ? micros : null;
}

export interface InsightAction {
  action_type?: string;
  value?: string | number;
}

export interface InsightRow {
  date_start?: string;
  hourly_stats_aggregated_by_advertiser_time_zone?: string;
  spend?: string | number;
  actions?: InsightAction[];
}

export interface MetaAggregate {
  spendCents: number;
  metaLeads: number;
  firstHalfSpend: boolean;
  secondHalfSpend: boolean;
}

export type AggregateResult = { ok: true; value: MetaAggregate } | { ok: false; message: string };

const LEAD_ACTIONS = ["lead", "offsite_conversion.fb_pixel_lead"] as const;

function sumLeadActions(actions: InsightAction[] | undefined, totals: Record<string, number>): boolean {
  for (const a of actions ?? []) {
    if (!a.action_type || !(LEAD_ACTIONS as readonly string[]).includes(a.action_type)) continue;
    const n = Number(a.value);
    if (!Number.isFinite(n) || n < 0) return false;
    totals[a.action_type] += n;
  }
  return true;
}

/** Sums the hourly rows that fall inside the window. Any row it can't read fails the whole sum. */
export function aggregateInsights(rows: InsightRow[], win: MonitorWindow, tz: string): AggregateResult {
  const splitMs = win.startMs + Math.floor(win.hours / 2) * HOUR_MS;
  const totals: Record<string, number> = { lead: 0, "offsite_conversion.fb_pixel_lead": 0 };
  let spendMicros = 0;
  let firstHalfSpend = false;
  let secondHalfSpend = false;
  for (const row of rows) {
    const hour = parseHourBucket(row.hourly_stats_aggregated_by_advertiser_time_zone);
    if (hour === null || !row.date_start || !/^\d{4}-\d{2}-\d{2}$/.test(row.date_start)) {
      return { ok: false, message: "Meta insights row without a readable date and hour" };
    }
    const bucketMs = zonedHourToUtcMs(row.date_start, hour, tz);
    if (bucketMs < win.startMs || bucketMs >= win.endMs) continue;
    const micros = parseSpendMicros(row.spend);
    if (micros === null) return { ok: false, message: "Meta reported a spend value that isn't a number" };
    if (!sumLeadActions(row.actions, totals)) return { ok: false, message: "Meta reported a lead count that isn't a number" };
    spendMicros += micros;
    if (micros > 0) {
      if (bucketMs < splitMs) firstHalfSpend = true;
      else secondHalfSpend = true;
    }
  }
  const metaLeads = Math.max(totals.lead, totals["offsite_conversion.fb_pixel_lead"]);
  if (!Number.isSafeInteger(spendMicros)) return { ok: false, message: "Meta's spend total is too large to add up exactly" };
  // Round once, at the end: summing per-row cents would drift on six-decimal hourly spend.
  const spendCents = Math.round(spendMicros / MICROS_PER_CENT);
  return { ok: true, value: { spendCents, metaLeads, firstHalfSpend, secondHalfSpend } };
}

// ── Evaluate ────────────────────────────────────────────────────────────────

export type FailureSource = "meta" | "db" | "config";

export interface Failure {
  source: FailureSource;
  message: string;
}

export interface SpaCounts {
  events: number;
  landing_page_leads: number;
  menu_inquiries: number;
  total: number;
}

export type Outcome =
  | { status: "read_failed"; failures: Failure[] }
  | { status: "no_spend" }
  | { status: "no_leads"; zero: Array<"spa" | "meta"> }
  | { status: "healthy" };

export interface EvaluateInput {
  windowHours: number;
  minSpendCents: number;
  failures: Failure[];
  meta: MetaAggregate | null;
  spa: SpaCounts | null;
}

export function evaluate(input: EvaluateInput): Outcome {
  const { failures, meta, spa } = input;
  if (failures.length > 0 || !meta || !spa) {
    return {
      status: "read_failed",
      failures: failures.length > 0 ? failures : [{ source: "config", message: "a read returned nothing" }],
    };
  }
  if (meta.spendCents <= 0 || meta.spendCents < input.minSpendCents) return { status: "no_spend" };
  if (input.windowHours >= 2 && (!meta.firstHalfSpend || !meta.secondHalfSpend)) return { status: "no_spend" };
  const zero: Array<"spa" | "meta"> = [];
  if (spa.total === 0) zero.push("spa");
  if (meta.metaLeads === 0) zero.push("meta");
  return zero.length > 0 ? { status: "no_leads", zero } : { status: "healthy" };
}

// ── State machine ───────────────────────────────────────────────────────────

export type IncidentKind = "no_leads" | "read_failed";

export type Transition =
  | { type: "none" }
  | { type: "open"; kind: IncidentKind }
  | { type: "touch" }
  | { type: "supersede"; kind: IncidentKind }
  | { type: "resolve" };

/** What to do given the open incident (if any) and this run's outcome. One alert per incident. */
export function decideTransition(openKind: IncidentKind | null, outcome: Outcome): Transition {
  const bad = outcome.status === "no_leads" || outcome.status === "read_failed" ? outcome.status : null;
  if (openKind === null) return bad ? { type: "open", kind: bad } : { type: "none" };
  if (bad) return bad === openKind ? { type: "touch" } : { type: "supersede", kind: bad };
  if (outcome.status === "healthy") return { type: "resolve" };
  // no_spend: a read failure is over (reads work again); a lead gap stays open until leads are seen.
  return openKind === "read_failed" ? { type: "resolve" } : { type: "none" };
}

/** Strips access tokens Meta sometimes echoes back, and caps the length. */
export function sanitizeMetaMessage(msg: string | undefined): string {
  return (msg ?? "unknown").replace(/access_token=[^&\s"']+/gi, "access_token=***").slice(0, 300);
}
